import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { LINKS } from "../lib/config";

export default function Nav({ onBook, variant = "transparent", theme = "fuchsia" }) {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const solid = variant === "solid" || scrolled;

    const links = [
        { label: "Home", to: "/", route: true },
        { label: "Chi sono", to: "/chi-sono", route: true },
        { label: "Percorsi", href: "/#percorsi" },
        { label: "Contatti", href: "/#contact" },
    ];

    return (
        <>
            <motion.header
                initial={{ y: -60, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.4, duration: 0.8 }}
                className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
                    solid ? "backdrop-blur-md bg-powder/80 border-b border-black/5" : ""
                }`}
            >
                <nav className="max-w-[1600px] mx-auto px-5 md:px-12 py-5 flex items-center justify-between">
                    <Link to="/" data-testid="nav-logo" className="flex items-center group">
                        <span className="brand-wordmark text-4xl md:text-5xl text-ink transition-transform duration-500 group-hover:scale-105">
                            <span className="text-fuchsia">#</span>CoachAmy
                        </span>
                    </Link>

                    <div className="hidden lg:flex items-center gap-10 font-sans text-sm">
                        {links.map((l) =>
                            l.route ? (
                                <Link
                                    key={l.label}
                                    to={l.to}
                                    className="editorial-link text-ink hover:text-fuchsia transition-colors"
                                    data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                >
                                    {l.label}
                                </Link>
                            ) : (
                                <a
                                    key={l.label}
                                    href={l.href}
                                    className="editorial-link text-ink hover:text-fuchsia transition-colors"
                                    data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                >
                                    {l.label}
                                </a>
                            )
                        )}
                    </div>

                    <div className="flex items-center gap-3">
                        <button
                            onClick={onBook}
                            data-testid="nav-book-btn"
                            className={`hidden lg:inline-flex ${theme === "accent" ? "btn-accent" : "btn-fuchsia"} !py-2.5 !px-5 text-sm leading-tight text-center max-w-[240px]`}
                        >
                            Prenota la tua consulenza gratuita
                        </button>
                        <button
                            className="lg:hidden p-2 -mr-2"
                            aria-label="menu"
                            data-testid="nav-menu-toggle"
                            onClick={() => setOpen(true)}
                        >
                            <Menu size={26} />
                        </button>
                    </div>
                </nav>
            </motion.header>

            {/* Mobile Menu */}
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[100] bg-ink text-white flex flex-col"
                    data-testid="mobile-menu"
                >
                    <div className="flex justify-between items-center px-5 py-5">
                        <span className="brand-wordmark text-4xl text-white">
                            <span className="text-fuchsia">#</span>CoachAmy
                        </span>
                        <button aria-label="close" onClick={() => setOpen(false)} data-testid="mobile-menu-close">
                            <X size={28} />
                        </button>
                    </div>
                    <div className="flex-1 flex flex-col justify-center px-8 gap-6">
                        {links.map((l, i) =>
                            l.route ? (
                                <motion.div key={l.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 * i }}>
                                    <Link to={l.to} onClick={() => setOpen(false)} className="font-serif text-4xl">
                                        {l.label}
                                    </Link>
                                </motion.div>
                            ) : (
                                <motion.a
                                    key={l.label}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.05 * i }}
                                    href={l.href}
                                    onClick={() => setOpen(false)}
                                    className="font-serif text-4xl"
                                >
                                    {l.label}
                                </motion.a>
                            )
                        )}
                        <button
                            onClick={() => {
                                setOpen(false);
                                onBook();
                            }}
                            className={`${theme === "accent" ? "btn-accent" : "btn-fuchsia"} mt-8 self-start`}
                            data-testid="mobile-book-btn"
                        >
                            Prenota una consulenza in presenza o online
                        </button>
                    </div>
                    <div className="px-8 pb-8 text-sm opacity-70 font-sans flex gap-6">
                        <a href={LINKS.instagram}>Instagram</a>
                        <a href={LINKS.whatsapp}>WhatsApp</a>
                        <a href={LINKS.email}>Email</a>
                    </div>
                </motion.div>
            )}
        </>
    );
}
